import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Footer from '../Footer';
import Header from '../Header';

const InfoPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const pages = ['shipping', 'returns', 'privacy', 'contact'];

  useEffect(() => {
    fetchPageContent();
  }, [slug]);

  const fetchPageContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      setPageData(data);
    } catch (error) {
      console.error('Error fetching page content:', error);
      // Use fallback content if Supabase fails
      setPageData(getFallbackContent(slug));
    } finally {
      setLoading(false);
    }
  };

  // Fallback content in case Supabase data isn't set up yet
  const getFallbackContent = (pageSlug) => {
    const fallbackData = {
      shipping: {
        title: 'Shipping',
        content: {
          sections: [
            {
              heading: 'Order Processing & Production',
              text: [
                'All items at Rtkartistry are made to order. This means your prints and apparel are produced specifically for you after your order is placed.',
              ],
              bullets: [
                'Production time: 3–7 business days',
                'Orders are processed Monday to Friday, excluding public holidays',
                'You will receive a confirmation email once your order is placed and another when it ships'
              ]
            },
            {
              heading: 'Shipping Destinations & Costs',
              text: [
                'We ship worldwide. Shipping costs are calculated at checkout based on your destination and order size.',
              ],
              bullets: [
                'All items are produced and shipped from Europe',
                'Tracking information is provided via email once your order has been dispatched'
              ]
            },
            {
              heading: 'Estimated Delivery Times',
              text: [
                'Delivery times begin after production is complete:'
              ],
              bullets: [
                'Europe (EU): 5–10 business days',
                'United Kingdom: 7–12 business days',
                'United States & Canada: 10–20 business days',
                'Rest of World: 15–25 business days'
              ]
            },
            {
              text: [
                'Please note: These are estimates and may vary due to customs processing, local postal services, or unforeseen circumstances. Rtkartistry is not responsible for delays caused by shipping carriers or customs.'
              ]
            },
            {
              heading: 'Customs & Import Duties',
              text: [
                'For international orders, your package may be subject to import duties and taxes, which are determined by your local customs authority. These charges are the responsibility of the recipient and are not included in our shipping costs.'
              ]
            }
          ]
        }
      },
      returns: {
        title: 'Returns',
        content: {
          sections: [
            {
              heading: 'Your Right to Return',
              text: [
                'In accordance with EU consumer protection laws, you have the right to return items within 14 days of delivery.'
              ]
            },
            {
              heading: 'Return Conditions',
              text: [
                'To be eligible for a return, items must be:'
              ],
              bullets: [
                'Unused, unworn, and unwashed',
                'In original condition with all tags attached',
                'Returned in original packaging where possible'
              ]
            },
            {
              heading: 'Made-to-Order Items',
              text: [
                'As our products are made to order, returns are only accepted if:'
              ],
              bullets: [
                'The item is defective or damaged upon arrival',
                'The wrong item was sent',
                'There is a significant quality issue'
              ]
            },
            {
              text: [
                'We do not accept returns for change of mind on made-to-order items, as they are produced specifically for you.'
              ]
            },
            {
              heading: 'How to Request a Return',
              bullets: [
                'Contact us at <a>info@rtkartistry.com</a> within 14 days of receiving your order',
                'Include your order number and reason for return',
                'We will provide return instructions and address',
                'Ship the item back to us (return shipping costs are the customer\'s responsibility unless the item is defective)'
              ]
            },
            {
              heading: 'Refund Process',
              bullets: [
                'Refunds are processed within 5–7 business days after we receive and inspect the returned item',
                'Refunds are issued to the original payment method',
                'Original shipping costs are non-refundable unless the return is due to our error or a defective product'
              ]
            },
            {
              heading: 'Damaged or Defective Items',
              text: [
                'If your item arrives damaged or defective:'
              ],
              bullets: [
                'Contact us within 48 hours of delivery',
                'Provide photos of the damage/defect',
                'We will arrange a replacement or full refund at no additional cost to you'
              ]
            }
          ]
        }
      },
      privacy: {
        title: 'Privacy Policy',
        content: {
          sections: [
            {
              heading: 'Introduction',
              text: [
                'Rtkartistry ("we", "our", "us") is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information when you visit our website or make a purchase.',
              ]
            },
            {
              heading: 'Information We Collect',
              text: [
                'We collect information that you provide directly to us, including:'
              ],
              bullets: [
                'Contact information: Name, email address, phone number (optional)',
                'Shipping information: Delivery address',
                'Payment information: Processed securely through Stripe (we do not store your card details)',
                'Order information: Products purchased, order history',
                'Communications: Any messages you send us'
              ]
            },
            {
              heading: 'How We Use Your Information',
              text: [
                'Your information is used to:'
              ],
              bullets: [
                'Process and fulfil your orders',
                'Send order confirmations, shipping updates, and delivery notifications',
                'Respond to your enquiries and provide customer support',
                'Improve our website and services',
                'Comply with legal obligations'
              ]
            },
            {
              heading: 'We Do Not',
              bullets: [
                'Sell your personal information to third parties',
                'Share your data for marketing purposes',
                'Send unsolicited marketing emails (unless you opt in)'
              ]
            },
            {
              heading: 'Data Sharing',
              text: [
                'We only share your information with:'
              ],
              bullets: [
                'Stripe: For secure payment processing',
                'Google Analytics: For anonymous website usage statistics (only if you consent to analytics cookies)',
                'Shipping partners: To deliver your orders',
                'Production partners: To fulfil your orders'
              ]
            },
            {
              text: [
                'All partners are contractually obligated to protect your data.'
              ]
            },
            {
              heading: 'Data Security',
              text: [
                'We implement appropriate technical and organisational measures to protect your personal information, including:'
              ],
              bullets: [
                'SSL encryption on our website',
                'Secure payment processing through Stripe (PCI-DSS compliant)',
                'Limited access to personal data on a need-to-know basis'
              ]
            },
            {
              heading: 'Your Rights',
              text: [
                'Under GDPR, you have the right to:'
              ],
              bullets: [
                'Access: Request a copy of the personal data we hold about you',
                'Rectification: Request correction of inaccurate data',
                'Erasure: Request deletion of your data (where applicable)',
                'Restriction: Request limitation of processing',
                'Portability: Receive your data in a portable format',
                'Object: Object to processing of your data'
              ]
            },
            {
              text: [
                'To exercise any of these rights, contact us at <a>info@rtkartistry.com</a>.'
              ]
            },
            {
              heading: 'Cookies',
              text: [
                'Our website uses cookies and similar technologies to enhance your experience.'
              ]
            },
            {
              heading: 'Essential Cookies',
              text: [
                'These are necessary for the website to function properly and cannot be disabled:'
              ],
              bullets: [
                'Cart functionality – Remembers items in your shopping cart',
                'Cookie consent – Remembers your cookie preferences',
                'Payment processing (Stripe) – Required for secure checkout'
              ]
            },
            {
              heading: 'Analytics Cookies (Optional)',
              text: [
                'With your consent, we use Google Analytics to understand how visitors use our site. This helps us improve our website and services. These cookies collect anonymous information including:'
              ],
              bullets: [
                'Pages visited and time spent on site',
                'How you arrived at our website',
                'General location (country/city level)',
                'Device and browser type'
              ]
            },
            {
              heading: 'Managing Your Cookie Preferences',
              text: [
                'When you first visit our website, you will be shown a cookie banner asking for your consent to use analytics cookies. You can:'
              ],
              bullets: [
                'Accept All – Enable all cookies including analytics',
                'Decline – Only essential cookies will be used'
              ]
            },
            {
              text: [
                'You can change your preference at any time by clearing your browser\'s local storage and refreshing the page, or by contacting us at <a>info@rtkartistry.com</a>.',
                'Disabling essential cookies in your browser settings may affect website functionality, such as your shopping cart.'
              ]
            },
            {
              heading: 'Data Retention',
              text: [
                'We retain your personal information for as long as necessary to fulfil your orders and provide customer support, comply with legal and accounting requirements, and resolve disputes.',
                'Order information is typically retained for 7 years for accounting purposes.'
              ]
            },
            {
              heading: 'Changes to This Policy',
              text: [
                'We may update this privacy policy from time to time. Changes will be posted on this page with an updated revision date.',
                'Last updated: March 2026'
              ]
            }
          ]
        }
      },
      contact: {
        title: 'Contact',
        content: {
          sections: [
            {
              heading: 'Get in Touch',
              text: [
                'Have a question about your order, a product, or just want to say hello? We\'d love to hear from you.'
              ]
            },
            {
              heading: 'Email',
              text: [
                '<a>info@rtkartistry.com</a>',
                'Please include your order number (if applicable) for faster assistance.'
              ]
            },
            {
              heading: 'Response Time',
              text: [
                'We aim to respond to all enquiries within 2–3 business days.'
              ]
            },
            {
              heading: 'Business Hours',
              text: [
                'Monday – Friday: 9:00 AM – 6:00 PM (EET)',
              ]
            },
            {
              heading: 'Social Media',
              text: [
                'Follow us for updates, new releases, and behind-the-scenes content:'
              ],
              bullets: [
                'Instagram: @rtk.artistry',
                'Pinterest: /rtk.artistry'
              ]
            }
          ]
        }
      }
    };

    return fallbackData[pageSlug] || { title: 'Page Not Found', content: { sections: [] } };
  };

  const renderContent = (content) => {
    if (!content || !content.sections) return null;

    return content.sections.map((section, index) => (
      <div key={index} style={{ marginBottom: '32px' }}>
        {section.heading && (
          <h2 style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#F1F1F1',
            marginBottom: '16px',
          }}>
            {section.heading}
          </h2>
        )}
        
        {section.text && section.text.map((paragraph, pIndex) => (
          <p
            key={pIndex}
            style={{
              fontSize: '14px',
              color: '#8A8A8A',
              lineHeight: '1.7',
              marginBottom: '12px',
            }}
            dangerouslySetInnerHTML={{
              __html: paragraph.replace(
                /<a>(.*?)<\/a>/g,
                '<a href="mailto:$1" style="color: #F1F1F1; text-decoration: none;">$1</a>'
              )
            }}
          />
        ))}

        {section.bullets && (
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: '12px 0',
          }}>
            {section.bullets.map((bullet, bIndex) => (
              <li
                key={bIndex}
                style={{
                  fontSize: '14px',
                  color: '#8A8A8A',
                  lineHeight: '1.7',
                  marginBottom: '8px',
                  paddingLeft: '16px',
                  position: 'relative',
                }}
                dangerouslySetInnerHTML={{
                  __html: `<span style="position: absolute; left: 0; color: #6F6F6F;">•</span>${bullet.replace(
                    /<a>(.*?)<\/a>/g,
                    '<a href="mailto:$1" style="color: #F1F1F1; text-decoration: none;">$1</a>'
                  )}`
                }}
              />
            ))}
          </ul>
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0D0D0D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#8A8A8A',
        fontFamily: "'TASA Orbiter Display', sans-serif",
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0D0D0D',
      fontFamily: "'TASA Orbiter Display', sans-serif",
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Helmet>
        <title>{slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Info'} | RTK Artistry</title>
        <meta name="description" content={`${slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Information'} - RTK Artistry`} />
      </Helmet>
      <Header />

      {/* Main Content */}
      <main style={{
        flex: 1,
        paddingTop: '120px',
        paddingBottom: '80px',
        paddingLeft: '20px',
        paddingRight: '20px',
      }}>
        {/* Page Title */}
        <h1 style={{
          fontSize: '32px',
          fontWeight: '400',
          color: '#F1F1F1',
          marginBottom: '24px',
        }}>
          {pageData?.title}
        </h1>

        {/* Page Tabs */}
        <div style={{
          display: 'flex',
          gap: '24px',
          marginBottom: '48px',
        }}>
          {pages.map((page) => (
            <Link
              key={page}
              to={`/${page}`}
              style={{
                color: slug === page ? '#F1F1F1' : '#6F6F6F',
                textDecoration: slug === page ? 'underline' : 'none',
                textUnderlineOffset: '4px',
                fontSize: '14px',
                fontWeight: '400',
                transition: 'color 0.2s ease',
                textTransform: 'capitalize',
              }}
            >
              {page}
            </Link>
          ))}
        </div>

        {/* Page Content */}
        <div style={{
          maxWidth: '600px',
        }}>
          {renderContent(pageData?.content)}
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default InfoPage;